import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ResourceEditor from "../components/ResourceEditor";
import { resourceFieldId } from "../utils/validation";
import { describeSchedule } from "../utils/schedule";
import { alertErrorClass } from "../../../dashboardStyles";
import {
  PlanLimits,
  PlanSeats,
  formatMoney,
} from "../../../../../constants/subscription";

// "Other" is not offered: nothing yet treats it differently from a space, so
// it was a third choice that changed nothing. A resource saved as Other before
// keeps the option, or its type would show as none selected.
const RESOURCE_TYPES = ["Person", "Space"];
const typesFor = (resource) =>
  resource?.type === "Other" ? [...RESOURCE_TYPES, "Other"] : RESOURCE_TYPES;
const ERROR_FIELDS = ["name", "email", "days", "hours"];

/**
 * Step 3: who takes the bookings.
 *
 * One resource is edited at a time. With more than one, the team is listed
 * above the editor — a row opens its resource, the bin beside it removes it;
 * with just the one (always the case on the free tier) there is no list and
 * nothing to remove, only the form.
 */
const ResourcesStep = ({
  resources,
  activeId,
  errors,
  plan,
  accountEmail,
  onSelect,
  onJumpToError,
  onAdd,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation();
  const active = resources.find((r) => r.id === activeId) ?? resources[0];

  // Only the free tier is capped — one specialist — and there the Add button
  // gives way to a note. Counts every resource regardless of type, mirroring
  // the backend's CountStaffAsync, which refuses the next one anyway.
  const maxStaff = PlanLimits[Number(plan)]?.maxStaff;
  const atCap = maxStaff != null && resources.length >= maxStaff;

  // Past the seats a paid plan includes, so the next one is chargeable: on
  // those plans nobody is stopped, they are quoted.
  const seatTerms = PlanSeats[Number(plan)];
  const nextSeatCosts =
    !atCap &&
    seatTerms?.extraSeatPrice != null &&
    resources.length >= seatTerms.includedSeats;

  const resourceLabel = (resource, index) =>
    resource.name?.trim()
      ? t("Dashboard.Wizard.Steps.resources.resourceNumberNamed", {
          number: index + 1,
          name: resource.name.trim(),
        })
      : t("Dashboard.Wizard.Steps.resources.resourceNumber", {
          number: index + 1,
        });

  // Every field that failed, named by the resource it is on. With several
  // people on the step the one that failed may not be the one open.
  const problems = resources.flatMap((resource, index) =>
    ERROR_FIELDS.map((field) => resourceFieldId(field, resource.id))
      .filter((fieldId) => errors[fieldId])
      .map((fieldId) => ({
        fieldId,
        resourceId: resource.id,
        message: errors[fieldId],
        label: resourceLabel(resource, index),
      })),
  );

  const hasErrors = (resource) =>
    ERROR_FIELDS.some((field) => errors[resourceFieldId(field, resource.id)]);

  return (
    <div className="mx-auto max-w-2xl">
      <h2 className="m-0 text-center text-2xl font-bold text-ink-900 sm:text-3xl">
        {t("Dashboard.Setup.team.title")}
      </h2>
      <p className="mx-auto mt-2 mb-6 max-w-xl text-center text-fluid-sm text-ink-500">
        {t("Dashboard.Setup.team.subtitle")}
      </p>

      {errors.resources && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errors.resources}</span>
        </div>
      )}

      {problems.length > 0 && (
        <div
          className={alertErrorClass}
          role="alert"
          data-testid="resource-errors-summary"
        >
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="m-0 font-semibold">
              {t("Dashboard.Wizard.Steps.resources.errorsSummary")}
            </p>
            <ul className="m-0 mt-1.5 list-disc space-y-1 pl-5">
              {problems.map(({ fieldId, resourceId, label, message }) => (
                <li key={fieldId}>
                  <button
                    type="button"
                    className="cursor-pointer font-semibold underline underline-offset-2 hover:text-red-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                    onClick={() => onJumpToError(fieldId, resourceId)}
                  >
                    {label}
                  </button>
                  : {message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {resources.length > 1 && (
        <ul
          className="m-0 mb-4 flex list-none flex-col gap-2 p-0"
          aria-label={t("Dashboard.Setup.team.listLabel")}
        >
          {resources.map((resource, index) => {
            const isActive = resource === active;
            return (
              <li key={resource.id} className="flex items-stretch gap-2">
                <button
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => onSelect(resource.id)}
                  className={`flex min-h-14 min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                    isActive
                      ? "border-brand-600 bg-brand-50"
                      : hasErrors(resource)
                        ? "border-red-300 bg-red-50 hover:border-red-400"
                        : "border-ink-200 bg-white hover:border-brand-300"
                  }`}
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-ink-100 text-sm font-semibold text-ink-600">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink-900">
                      {resource.name?.trim() ||
                        t("Dashboard.Wizard.Steps.resources.resourceNumber", {
                          number: index + 1,
                        })}
                    </span>
                    <span className="block truncate text-xs text-ink-500">
                      {describeSchedule(resource, t)}
                    </span>
                  </span>
                  <i
                    className={`bi ${isActive ? "bi-pencil-fill text-brand-600" : "bi-chevron-right text-ink-400"}`}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(resource.id)}
                  aria-label={t("Dashboard.Wizard.resourceItem.removeAriaLabel", {
                    name:
                      resource.name ||
                      t("Dashboard.Wizard.resourceItem.resourceFallbackName"),
                  })}
                  className="inline-flex w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-ink-200 bg-white text-ink-500 transition hover:border-red-500 hover:text-red-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {active && (
        <ResourceEditor
          key={active.id}
          resource={active}
          resourceTypes={typesFor(active)}
          accountEmail={accountEmail}
          errors={errors}
          onUpdate={onUpdate}
        />
      )}

      {atCap && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
          data-testid="plan-staff-note"
        >
          <i className="bi bi-info-circle-fill mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />
          <span>{t("Dashboard.Setup.team.startIncludesOne")}</span>
        </div>
      )}

      {/* Loud on purpose. This is the only warning a business gets that the
          next person they add costs money, and it was previously a grey line
          the same size as the help text above it — read past, and then met as a
          surprise on the first invoice. The price is the part that has to
          survive a glance, so it is what carries the weight. */}
      {nextSeatCosts && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3 text-fluid-sm font-semibold text-red-700"
          role="alert"
          data-testid="seat-cost-warning"
        >
          <i
            className="bi bi-exclamation-triangle-fill mt-0.5 shrink-0 text-base"
            aria-hidden="true"
          />
          <span>
            {t("Dashboard.Plan.seatCostHint", {
              price: formatMoney(seatTerms.extraSeatPrice),
            })}
          </span>
        </div>
      )}

      {!atCap && (
        <button
          type="button"
          className="mt-4 flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-card border-2 border-dashed border-ink-200 bg-white text-sm font-semibold text-brand-700 transition hover:border-brand-600 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          onClick={onAdd}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          {t("Dashboard.Wizard.Steps.resources.addButton")}
        </button>
      )}
    </div>
  );
};

ResourcesStep.propTypes = {
  resources: PropTypes.array.isRequired,
  /** The resource open in the editor; the first one when unset. */
  activeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  errors: PropTypes.object.isRequired,
  plan: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  accountEmail: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  /** (fieldId, resourceId) — open that resource and put the field in focus. */
  onJumpToError: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ResourcesStep;
